/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useContext, useState, useEffect, useCallback, useMemo} from 'react'
import {useLocation} from 'react-router-dom'
import PropTypes from 'prop-types'
import {useIntl, FormattedMessage} from 'react-intl'
import {
    Box,
    Button,
    Flex,
    Text,
    Modal,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalOverlay,
    Stack,
    useBreakpointValue,
    ModalFooter
} from '@chakra-ui/react'
import {useCurrentBasket} from './use-current-basket'
import Link from '../components/link'
import RecommendedProducts from '../components/recommended-products'
import {LockIcon} from '../components/icons'
import Callout from '../components/content-asset'
import textStyles from '../theme/foundations/textStyles'
import useMultiSite from '../hooks/use-multi-site'
import {CART_HREF, CRO_LOCATION} from '../constants'
import {useCroSlot} from '../components/cro'
import {getProductsToAddToCart} from '../utils/product-utils'
import {useCookieConsent} from './use-cookie-consent'
import RecommendedAccessories from '../components/recommended-accessories'
import InteractionStudioRecommendedProducts from '../components/interaction-studio/recommended-products'
import PayLater from '../components/pay-later'
import {useD2BCustomer} from './use-d2b-customer'
import {calculateNetPrice} from '../utils/utils'
import useEffectEvent from './use-effect-event'
import {useAddToCartImplementation} from './use-add-to-cart'
import {
    BonusProductsModalContext,
    BonusProductsModal,
    useBonusProductsModal
} from './use-bonus-products-modal'

/**
 * @typedef {{
 *   onClose: function,
 *   isOpen: boolean,
 *   data?: {product: object, itemsAdded: object[]},
 *   onOpen: function({onRecommendedProductClick?: function, onClose?: function}=),
 *   addProduct: function(product: object),
 *   onRecommendedProductClick?: function
 * }} AddToCartModalContextType
 */

/**
 * @typedef {{
 *   addProduct: function({
 *     product: object,
 *     includeInAddToCartModal?: boolean,
 *     showAddToCartModal?: boolean,
 *     onRecommendedProductClick?: function,
 *     onAfterAddToCart?: function,
 *     engraveText?: string
 *   }),
 * }} AddToCartActionContextType
 */

/**
 * This is the context for managing adding products to cart with associated visual feedback.
 * Used in top level App component.
 * @type {React.Context<{modal: AddToCartModalContextType, action: AddToCartActionContextType}|null>}
 */
export const AddToCartModalContext = React.createContext(null)

/**
 * Hook to access the AddToCartModalContextType.
 * @return {AddToCartModalContextType}
 */
export const useAddToCartModalContext = () => {
    const context = useContext(AddToCartModalContext)
    if (!context) {
        throw new Error('useAddToCartModalContext must be used within a AddToCartModalProvider')
    }
    return context.modal
}

/**
 * Hook to access the AddToCartActionContextType.
 * @return {AddToCartActionContextType}
 */
export const useAddToCartActionContext = () => {
    const context = useContext(AddToCartModalContext)
    if (!context) {
        throw new Error('useAddToCartActionContext must be used within a AddToCartModalProvider')
    }
    return context.action
}

/**
 * Provider for the AddToCartModalContext.
 * @param children
 */
export const AddToCartModalProvider = ({children}) => {
    const modalContext = useAddToCartModal()
    const {context: actionContext, children: actionChildren} = useAddToCartAction()
    const bonusContext = useBonusProductsModal()
    const context = useMemo(
        () => ({modal: modalContext, action: actionContext}),
        [modalContext, actionContext]
    )
    return (
        <AddToCartModalContext.Provider value={context}>
            <BonusProductsModalContext.Provider value={bonusContext}>
                {children}
                {actionChildren}
                <AddToCartModal />
                <BonusProductsModal />
            </BonusProductsModalContext.Provider>
        </AddToCartModalContext.Provider>
    )
}
AddToCartModalProvider.propTypes = {
    children: PropTypes.node.isRequired
}

const RecommendedProductsWrapper = ({children}) => (
    <Box paddingTop="2rem" className="is-atc-recommendation">
        {children}
    </Box>
)
RecommendedProductsWrapper.propTypes = {
    children: PropTypes.node
}

/**
 * Visual feedback (a modal) for adding item to the cart.
 */
export const AddToCartModal = () => {
    const {isOpen, onClose, data, onRecommendedProductClick} = useAddToCartModalContext()
    const {product, itemsAdded = [], count: quantity = itemsAdded.length} = data || {}
    const intl = useIntl()
    const {
        data: basket = {},
        derivedData: {totalItems}
    } = useCurrentBasket()
    const size = useBreakpointValue({lg: '2xl', xl: '4xl'})
    const itemsPerSlide = useBreakpointValue({base: 2, lg: 3})
    const {marketing: marketingCookieAccepted} = useCookieConsent()
    /**
     * [isCentered] is used to:
     * - Center the modal vertically on Desktop (Default - on mobile view modal is attached to bottom)
     * - [isCentered] = false ? Mobile : Desktop
     * - Hide (Cart items & Order details) on mobile view when recommendations are available
     */
    const isCentered = useBreakpointValue({base: false, lg: true})
    const {site} = useMultiSite()
    const isD2BEnabled = useD2BCustomer().enabled
    useCroSlot(CRO_LOCATION.ADD_TO_CART_MODAL, isOpen)

    if (!isOpen) {
        return null
    }
    const {currency, productSubTotal} = basket

    const isCart = /\/appcart$/.test(location?.pathname)

    return (
        <Modal
            size={size}
            isOpen={isOpen}
            onClose={onClose}
            isCentered={isCentered}
            variant="cart"
            colorScheme="stokkeGray"
            returnFocusOnClose={false}
        >
            <ModalOverlay />
            <ModalContent containerProps={{'data-testid': 'add-to-cart-modal'}}>
                <ModalHeader>
                    <Text role="status" textStyle="Regular 2XLarge">
                        {intl.formatMessage(
                            {
                                defaultMessage:
                                    '{quantity} {quantity, plural, one {item} other {items}} added to cart',
                                id: 'add_to_cart_modal.info.added_to_cart'
                            },
                            {quantity}
                        )}
                    </Text>
                </ModalHeader>
                {/* fill the space above footer on lg */}
                <Box bgColor="stokkeCore.white" />
                <ModalCloseButton />
                <ModalBody>
                    <Stack spacing={4} flex={1} paddingBottom={{base: 6, lg: 12}}>
                        <InteractionStudioRecommendedProducts id="is-atc-recommendations">
                            {marketingCookieAccepted ? (
                                <RecommendedProducts
                                    wrapper={RecommendedProductsWrapper}
                                    recommender={`recom_Basket_${site.id}`}
                                    products={[product]}
                                    shouldFetch={() => product?.id}
                                    onRecommendedProductClick={onRecommendedProductClick}
                                    itemsPerSlide={itemsPerSlide}
                                    title={
                                        <FormattedMessage
                                            defaultMessage="You Might Also Like"
                                            id="add_to_cart_modal.recommended_products.title.might_also_like"
                                        />
                                    }
                                />
                            ) : (
                                <RecommendedAccessories
                                    wrapper={RecommendedProductsWrapper}
                                    products={[product]}
                                    shouldFetch={() => product?.id}
                                    onRecommendedProductClick={onRecommendedProductClick}
                                    itemsPerSlide={itemsPerSlide}
                                    title={
                                        <FormattedMessage
                                            defaultMessage="You Might Also Like"
                                            id="add_to_cart_modal.recommended_products.title.might_also_like"
                                        />
                                    }
                                />
                            )}
                        </InteractionStudioRecommendedProducts>
                    </Stack>
                </ModalBody>
                <ModalFooter>
                    <Box order={-2}>
                        <Flex justifyContent="space-between" mb={4}>
                            <Text {...textStyles['Regular Medium']}>
                                {intl.formatMessage({
                                    defaultMessage: 'Just added',
                                    id: 'add_to_cart_modal.label.just_added'
                                })}
                            </Text>
                            {isD2BEnabled ? (
                                <Text
                                    alignSelf="flex-end"
                                    {...textStyles['Regular Medium']}
                                    data-testid="product-price"
                                >
                                    {product.price &&
                                        intl.formatNumber(
                                            calculateNetPrice(product.price, product.c_taxRate),
                                            {
                                                style: 'currency',
                                                currency: product.currency || currency
                                            }
                                        )}
                                </Text>
                            ) : (
                                <Text
                                    alignSelf="flex-end"
                                    {...textStyles['Regular Medium']}
                                    data-testid="product-price"
                                >
                                    {product.price &&
                                        intl.formatNumber(product.price, {
                                            style: 'currency',
                                            currency: product.currency || currency
                                        })}
                                </Text>
                            )}
                        </Flex>
                        <Flex justifyContent="space-between">
                            <Text {...textStyles['Regular Medium']}>
                                {intl.formatMessage(
                                    {
                                        defaultMessage:
                                            'Cart subtotal ({itemAccumulatedCount, plural, =0 {0 items} one {# item} other {# items}})',
                                        id: 'add_to_cart_modal.label.cart_subtotal'
                                    },
                                    {itemAccumulatedCount: totalItems}
                                )}
                            </Text>
                            {isD2BEnabled ? (
                                <Text
                                    alignSelf="flex-end"
                                    {...textStyles['Regular Medium']}
                                    data-testid="cart-subtotal"
                                >
                                    {productSubTotal &&
                                        intl.formatNumber(basket.c_productSubTotalNetPrice, {
                                            style: 'currency',
                                            currency: currency
                                        })}
                                </Text>
                            ) : (
                                <Text
                                    alignSelf="flex-end"
                                    {...textStyles['Regular Medium']}
                                    data-testid="cart-subtotal"
                                >
                                    {productSubTotal &&
                                        intl.formatNumber(productSubTotal, {
                                            style: 'currency',
                                            currency: currency
                                        })}
                                </Text>
                            )}
                        </Flex>
                    </Box>

                    <PayLater
                        amount={productSubTotal}
                        currency={currency}
                        paypalPlacement="cart"
                        klarnaPlacement="credit-promotion-badge"
                    />

                    <Stack order={{base: 0, lg: -1}} spacing="4">
                        <Button
                            as={Link}
                            to={CART_HREF}
                            onClick={() => isCart && onClose()}
                            width="100%"
                            variant="outline"
                            size="lg"
                            {...textStyles['Regular Medium']}
                        >
                            {intl.formatMessage({
                                defaultMessage: 'View Cart',
                                id: 'add_to_cart_modal.link.view_cart'
                            })}
                        </Button>

                        <Button
                            as={Link}
                            to="/appcheckout"
                            width="100%"
                            variant="placeOrder"
                            size="lg"
                            rightIcon={<LockIcon />}
                            {...textStyles['Regular Medium']}
                        >
                            {intl.formatMessage({
                                defaultMessage: 'Proceed to Checkout',
                                id: 'add_to_cart_modal.link.checkout'
                            })}
                        </Button>
                    </Stack>

                    <Callout id="pwa-addtocart-usp" />
                </ModalFooter>
            </ModalContent>
        </Modal>
    )
}

AddToCartModal.propTypes = {
    product: PropTypes.shape({
        name: PropTypes.string,
        imageGroups: PropTypes.array
    }),
    itemsAdded: PropTypes.array,
    variant: PropTypes.shape({
        productId: PropTypes.string,
        variationValues: PropTypes.object
    }),
    quantity: PropTypes.number,
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    children: PropTypes.any
}

/**
 * Hook for managing the AddToCartModal state.
 * @return {AddToCartModalContextType}
 */
export const useAddToCartModal = () => {
    const [state, setState] = useState({
        isOpen: false,
        data: null
    })

    const {pathname} = useLocation()
    useEffect(() => {
        setState((state) =>
            state.isOpen
                ? {
                      ...state,
                      isOpen: false,
                      data: null
                  }
                : state
        )
    }, [pathname])

    return {
        isOpen: state.isOpen,
        data: state.data,
        onRecommendedProductClick: state.onRecommendedProductClick,
        onOpen: ({onRecommendedProductClick, onClose} = {}) => {
            setState((oldState) => ({
                ...oldState,
                isOpen: true,
                onRecommendedProductClick,
                onClose
            }))
        },
        onClose: () => {
            state?.onClose?.()
            setState({
                isOpen: false,
                data: null
            })
        },
        addProduct: (product, quantity = 1) => {
            setState((oldState) => {
                if (!oldState.data) {
                    const itemsAdded = getProductsToAddToCart(product)

                    return {
                        ...oldState,
                        data: {
                            product,
                            itemsAdded,
                            count: itemsAdded.length * quantity
                        }
                    }
                } else {
                    return {
                        ...oldState,
                        data: {
                            ...oldState.data,
                            itemsAdded: [...oldState.data.itemsAdded, product],
                            count: oldState.data.count + quantity
                        }
                    }
                }
            })
        }
    }
}

/**
 * A sequence to use to generate unique ids for products to add to cart.
 * @type {number}
 */
let sequence = 0

/**
 * Hook for handling the add to cart action context.
 * @return {{context: AddToCartActionContextType, children: React.ReactNode}}
 */
const useAddToCartAction = () => {
    // keep the product to add requests in a state to handle adding the product to cart after all dependent data is loaded
    const [requests, setRequests] = useState([])

    const addProduct = useCallback(
        ({
            product,
            quantity,
            includeInAddToCartModal = true,
            showAddToCartModal = true,
            onRecommendedProductClick,
            onAfterAddToCart,
            engraveText
        }) => {
            setRequests((existingProducts) => [
                ...existingProducts,
                {
                    product,
                    quantity,
                    includeInAddToCartModal,
                    showAddToCartModal,
                    onRecommendedProductClick,
                    onAfterAddToCart,
                    engraveText,
                    id: sequence++
                }
            ])
        },
        []
    )

    // mark requests from old pages to skip showing modals
    const {pathname} = useLocation()
    useEffect(() => {
        setRequests((list) =>
            list.map((item) => (item.fromOldPage ? item : {...item, fromOldPage: true}))
        )
    }, [pathname])

    const context = useMemo(() => ({addProduct}), [addProduct])
    return {
        context,
        children: requests.map(({id, ...productToAdd}) => (
            <AddToCartAction
                {...productToAdd}
                // use the unique id to prevent recreation of the component when the list changes
                key={id}
                onFinished={() =>
                    // remove the request from the list after it has been processed
                    setRequests((list) => list.filter((item) => item.id !== id))
                }
            />
        ))
    }
}

/**
 * Add to cart action component - executes adding to cart logic with display of bonus product modal and add to cart modal.
 */
const AddToCartAction = ({
    fromOldPage,
    engraveText,
    product,
    quantity,
    includeInAddToCartModal,
    showAddToCartModal,
    onAfterAddToCart,
    onFinished,
    onRecommendedProductClick
}) => {
    const {handleAddToCart, isLoading} = useAddToCartImplementation(product, {
        fromOldPage,
        includeInAddToCartModal,
        quantity,
        showAddToCartModal,
        onFinished,
        onRecommendedProductClick
    })
    const onLoaded = useEffectEvent(() => {
        handleAddToCart({onAfterAddToCart, engraveText})
    })
    useEffect(() => {
        if (!isLoading) {
            onLoaded()
        }
    }, [isLoading, onLoaded])
    return null
}
AddToCartAction.propTypes = {
    fromOldPage: PropTypes.bool,
    engraveText: PropTypes.string,
    product: PropTypes.object,
    quantity: PropTypes.number,
    includeInAddToCartModal: PropTypes.bool,
    showAddToCartModal: PropTypes.bool,
    onAfterAddToCart: PropTypes.func,
    onFinished: PropTypes.func,
    onRecommendedProductClick: PropTypes.func
}
